import { useCallback, useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  ApiError,
  cancelReservation,
  checkInReservation,
  completeReservation,
  markReservationNoShow,
} from '../api-client.ts';
import { tokens } from '../ui/tokens.ts';

interface Params {
  readonly onChanged: () => void;
}

interface ActionSpec {
  readonly do: (id: string) => Promise<unknown>;
  readonly successTitle: string;
  readonly successMsg: string;
  readonly successColor: string;
  readonly failureTitle: string;
}

interface RowRef {
  readonly reservationId: string;
}

const ACTIONS = {
  checkIn: {
    do: checkInReservation,
    successTitle: '客人到囉 🎉',
    successMsg: '已切換為進行中',
    successColor: tokens.color.mantine['lifecycleCheckIn']!,
    failureTitle: '報到失敗',
  },
  complete: {
    do: completeReservation,
    successTitle: '場次結束 ✨',
    successMsg: '格子已釋放',
    successColor: tokens.color.mantine['neutral']!,
    failureTitle: '完成失敗',
  },
  cancel: {
    do: (id: string) => cancelReservation(id, null),
    successTitle: '已取消',
    successMsg: '',
    successColor: tokens.color.mantine['neutral']!,
    failureTitle: '取消失敗',
  },
  noShow: {
    do: (id: string) => markReservationNoShow(id),
    successTitle: '已標記未到',
    successMsg: '已計入客人 no-show 次數',
    successColor: tokens.color.mantine['danger']!,
    failureTitle: '標記失敗',
  },
} as const satisfies Record<string, ActionSpec>;

export function useReservationActions(p: Params) {
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());

  const run = useCallback(
    async (spec: ActionSpec, r: RowRef) => {
      setBusyIds((prev) => new Set([...prev, r.reservationId]));
      try {
        await spec.do(r.reservationId);
        notifications.show({
          color: spec.successColor,
          title: spec.successTitle,
          message: spec.successMsg,
        });
        p.onChanged();
      } catch (e) {
        notifications.show({
          color: tokens.color.mantine['danger']!,
          title: spec.failureTitle,
          message: e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e),
        });
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(r.reservationId);
          return next;
        });
      }
    },
    [p],
  );

  return {
    isBusy: (id: string) => busyIds.has(id),
    checkIn: (r: RowRef) => run(ACTIONS.checkIn, r),
    complete: (r: RowRef) => run(ACTIONS.complete, r),
    cancel: (r: RowRef) => run(ACTIONS.cancel, r),
    noShow: (r: RowRef) => run(ACTIONS.noShow, r),
  };
}
